import { yupResolver } from '@hookform/resolvers/yup'
import { getErrorMessage, type FormContainerOnError, type FormSetup } from '@if/mui'
import { InferType } from '@if/core/yup'
import { Stack } from '@mui/material'
import { useUpdateMerchant, type UpdateMerchantDto } from '@src/data/api/merchants-api/merchants-api'
import { EMerchantProperties, EMerchantReportFrequency } from '@src/data/types/Constants'
import { Merchant } from '@src/data/types/Merchant'
import { AxiosError } from 'axios'
import { useCallback, useMemo } from 'react'
import { FieldValues, UseFormReturn } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { buildUpdateMerchantDto } from '../buildUpdateMerchantDto'
import MerchantBuyDownPanel, { MerchantBuyDownSchema } from '../MerchantBuyDownPanel'
import MerchantPropertiesPanel, { MerchantPropertiesSchema } from '../MerchantPropertiesPanel'
import MerchantRatesPanel from '../MerchantRatesPanel'
import MerchantReportConfigPanel, { MerchantReportFormSchema } from '../MerchantReportConfigPanel'
import MerchantTagsPanel, { MerchantTagsSchema } from '../MerchantTagsPanel'
import MerchantTerritory, { MerchantTerritorySchema } from '../MerchantTerritory'
import { useSyncFormDefaults } from '../useSyncFormDefaults'

export const MerchantGeneralFormSchema = MerchantPropertiesSchema.concat(MerchantTagsSchema)
  .concat(MerchantTerritorySchema)
  .concat(MerchantReportFormSchema)
  .concat(MerchantBuyDownSchema)

export type MerchantGeneralFormValues = InferType<typeof MerchantGeneralFormSchema>

const merchantPropertyKeys = Object.values(EMerchantProperties)

function getMerchantGeneralDefaults(merchant: Merchant): MerchantGeneralFormValues {
  const properties = merchantPropertyKeys.reduce(
    (acc, key) => {
      acc[key] = merchant[key]
      return acc
    },
    {} as Record<EMerchantProperties, boolean>,
  )

  return {
    tags: merchant.tags ?? [],
    territoryId: merchant.territoryId,
    reportGenerationFrequency: merchant.reportGenerationFrequency,
    reportRecipientsEmailList: merchant.reportRecipientsEmailList,
    bannerMerchantFeeRate: merchant.bannerMerchantFeeRate || undefined,
    flashPlanRateRebate: merchant.flashPlanRateRebate,
    lenderAdminFee: merchant.lenderAdminFee,
    ...properties,
  }
}

function toGeneralOverrides(values: MerchantGeneralFormValues): Partial<UpdateMerchantDto> {
  return {
    territoryId: values.territoryId,
    tags: values.tags,
    reportGenerationFrequency: !values.reportGenerationFrequency
      ? EMerchantReportFrequency.never
      : values.reportGenerationFrequency,
    reportRecipientsEmailList: values.reportRecipientsEmailList,
    noPaymentPolicy: values.noPaymentPolicy,
    hideAddress: values.hideAddress,
    canSkipFlinks: values.canSkipFlinks,
    canPayBorrowers: values.canPayBorrowers,
    automaticIncomeApproval: values.automaticIncomeApproval,
    canAcceptHighEstimate: values.canAcceptHighEstimate,
    showInterestRateOnMerchantDashboard: values.showInterestRateOnMerchantDashboard,
    bannerMerchantFeeRate: values.bannerMerchantFeeRate ?? null,
    flashPlanRateRebate: values.flashPlanRateRebate ?? null,
    lenderAdminFee: values.lenderAdminFee ?? null,
  }
}

interface MerchantGeneralTabContentProps {
  merchant: Merchant
  defaults: MerchantGeneralFormValues
}

const MerchantGeneralTabContent = ({ merchant, defaults }: MerchantGeneralTabContentProps) => {
  useSyncFormDefaults<MerchantGeneralFormValues>(defaults)

  return (
    <Stack gap={2}>
      <MerchantTerritory merchant={merchant} />
      <MerchantTagsPanel />
      <MerchantRatesPanel merchant={merchant} />
      <MerchantReportConfigPanel />
      <MerchantPropertiesPanel bannerOwnedFields={merchant.bannerOwnedFields ?? []} />
      <MerchantBuyDownPanel bannerOwnedFields={merchant.bannerOwnedFields ?? []} />
    </Stack>
  )
}

export interface MerchantTabConfig<T extends FieldValues> {
  useFormSetup: () => FormSetup<T>
  onSubmit: (values: T, methods: UseFormReturn<T>) => unknown
  onError?: FormContainerOnError<T>
  children: (methods: UseFormReturn<T>) => React.ReactNode
}

/**
 * Builds the General tab's form config (schema/defaults/save) and body. Returned
 * as a config object so EditMerchant can spread it onto a `<MultiFormTab>`.
 */
export function useMerchantGeneralTab(merchant: Merchant): MerchantTabConfig<MerchantGeneralFormValues> {
  const { t } = useTranslation()
  const [updateMerchant] = useUpdateMerchant()

  const defaults = useMemo(() => getMerchantGeneralDefaults(merchant), [merchant])

  const useFormSetup = useCallback(
    (): FormSetup<MerchantGeneralFormValues> => ({
      resolver: yupResolver(MerchantGeneralFormSchema),
      mode: 'onBlur',
      defaultValues: defaults,
    }),
    [defaults],
  )

  const onSubmit = useCallback(
    async (values: MerchantGeneralFormValues, methods: UseFormReturn<MerchantGeneralFormValues>) => {
      await updateMerchant(buildUpdateMerchantDto(merchant, toGeneralOverrides(values)))
      methods.reset(values)
    },
    [merchant, updateMerchant],
  )

  // Preserves the friendly messages the page used to show in a Snackbar, now
  // routed through FormContainer's error surface (ErrorAlert above the fields).
  const onError = useCallback<FormContainerOnError<MerchantGeneralFormValues>>(
    (error) => {
      const axiosError = error as AxiosError<string>
      if (axiosError?.response?.data === 'Dashboard access required for High Estimate') {
        return t('common.errors.dashboardAccessRequiredForHighEstimate')
      }

      const message = getErrorMessage(error)
      if (message?.includes('owned by the parent banner')) {
        // Extracts the field list after the last colon, e.g. "Banner owned fields: Foo, Bar." -> "Foo, Bar"
        const fields = /:\s*(.+?)\.?\s*$/.exec(message)?.[1] ?? null
        if (fields !== null) return t('merchant.bannerOwnedWriteGuardError', { fields })
      }
    },
    [t],
  )

  const children = useCallback(() => <MerchantGeneralTabContent merchant={merchant} defaults={defaults} />, [merchant, defaults])

  return { useFormSetup, onSubmit, onError, children }
}
